import React, { useState, useEffect } from "react";
import { X, Loader2, Mail, MapPin, CheckCircle, ArrowLeft } from "lucide-react";
import UIPhoneInput from 'components/core/phone-input/phone-input';
import { Value } from 'react-phone-number-input';
import { validatePhoneNumber } from 'utils/validation/phone-validation';
import * as z from 'zod';
import { useTranslation } from 'react-i18next';
import {
    Dialog,
    DialogContent,
    DialogTitle,
} from 'components/ui/dialog';
import { Button } from "components/ui/button";

interface ContactFormData {
    name: string;
    company: string;
    email: string;
    phone?: string;
    message: string;
}

interface ContactModalProps {
    isOpen: boolean;
    onClose: () => void;
    onSubmit: (data: ContactFormData) => Promise<any>;
    title?: string;
    initialData?: Partial<ContactFormData>;
    companyRequired?: boolean;
}



const ContactModal: React.FC<ContactModalProps> = ({ isOpen, onClose, onSubmit, title, initialData, companyRequired = false }) => {
    const { t } = useTranslation();

    const createContactSchema = () =>
        z.object({
            name: z.string().min(1, t('NAME_IS_REQUIRED')).max(50, t('NAME_IS_TOO_LONG')),
            company: companyRequired
                ? z.string().min(1, t('COMPANY_IS_REQUIRED')).max(100, t('COMPANY_IS_TOO_LONG'))
                : z.string().max(100, t('COMPANY_IS_TOO_LONG')).optional().or(z.literal('')),
            email: z.string()
                .min(1, t('EMAIL_IS_REQUIRED'))
                .email(t('INVALID_EMAIL_ADDRESS'))
                .refine(
                    (val) => /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/.test(val),
                    { message: t('INVALID_EMAIL_ADDRESS') }
                ),
            phone: z.string()
                .refine(
                    (val) => !val || /^\+[1-9]\d{1,14}$/.test(val),
                    { message: t('INVALID_PHONE_NUMBER') }
                )
                .refine(
                    validatePhoneNumber,
                    { message: t('PHONE_NUMBER_TOO_SHORT') }
                )
                .optional()
                .or(z.literal('')),
            message: z.string().min(1, t('MESSAGE_IS_REQUIRED')),
        });

    const [formData, setFormData] = useState<ContactFormData>({
        name: initialData?.name || "",
        company: initialData?.company || "",
        email: initialData?.email || "",
        phone: initialData?.phone || "",
        message: initialData?.message || "",
    });
    const [errors, setErrors] = useState<Record<string, string>>({});
    const [loading, setLoading] = useState(false);
    const [isSuccess, setIsSuccess] = useState(false);

    useEffect(() => {
        // eslint-disable-next-line no-console
        console.log('useEffect triggered - isOpen:', isOpen, 'isSuccess:', isSuccess);
        if (isOpen && !isSuccess) {
            // eslint-disable-next-line no-console
            console.log('Resetting form to initial state');
            setFormData({
                name: initialData?.name || "",
                company: initialData?.company || "",
                email: initialData?.email || "",
                phone: initialData?.phone || "",
                message: initialData?.message || "",
            });
            setErrors({});
            setLoading(false);
        }
        if (!isOpen) {
            // Only reset success state when modal is closed
            // eslint-disable-next-line no-console
            console.log('Modal closed, resetting success state');
            setIsSuccess(false);
        }
    }, [isOpen, initialData, isSuccess]);

    // Debug: Track isSuccess state changes
    useEffect(() => {
        // eslint-disable-next-line no-console
        console.log('isSuccess state changed to:', isSuccess);
    }, [isSuccess]);

    const schema = createContactSchema();

    const validate = () => {
        try {
            schema.parse(formData);
            setErrors({});
            return true;
        } catch (error) {
            if (error instanceof z.ZodError) {
                const newErrors: Record<string, string> = {};
                error.errors.forEach((err) => {
                    if (err.path[0]) {
                        newErrors[err.path[0].toString()] = err.message;
                    }
                });
                setErrors(newErrors);
            }
            return false;
        }
    };

    const handleSubmit = async () => {
        if (validate()) {
            try {
                setLoading(true);
                const isOnlyCode =
                    formData.phone?.match(/^\+\d{1,4}$/) && formData.phone.length <= 4;

                // eslint-disable-next-line no-console
                console.log('Submitting contact form...');
                await onSubmit({
                    ...formData,
                    phone: isOnlyCode ? "" : formData.phone,
                });

                // Only set success if no error was thrown
                // eslint-disable-next-line no-console
                console.log('Contact form submitted successfully');
                // eslint-disable-next-line no-console
                console.log('Setting isSuccess to true');
                setIsSuccess(true);
                // eslint-disable-next-line no-console
                console.log('isSuccess state updated');
            } catch (error) {
                // eslint-disable-next-line no-console
                console.error("Submission failed:", error);
                // Don't set success on error
            } finally {
                setLoading(false);
            }
        }
    };

    return (
        <Dialog open={isOpen} onOpenChange={onClose}>
            <DialogContent className="max-w-6xl w-[95vw] max-h-[95vh] sm:max-h-[90vh] overflow-y-auto p-0" hideClose>
                <DialogTitle className="sr-only">{title || t('CONTACT_SALES')}</DialogTitle>
                {/* Header */}
                <div className="flex items-center justify-between px-4 sm:px-8 pt-3 pb-0 sm:pt-3 sm:pb-0">
                    <h2 className="text-lg sm:text-xl font-semibold text-foreground">{title || t('CONTACT_SALES')}</h2>
                    <button
                        onClick={onClose}
                        className="p-2 rounded-full text-muted-foreground hover:text-foreground hover:bg-accent transition-colors"
                    >
                        <X size={18} className="sm:w-5 sm:h-5" />
                    </button>
                </div>
                {/* Main Content */}
                <div className="flex flex-col lg:flex-row flex-1 border-t border-border">
                    {/* Left Side - Contact Information */}
                    <div className="bg-muted/30 border-r-0 lg:border-r border-b lg:border-b-0 border-border p-4 sm:p-6 lg:p-8 lg:w-2/5 flex flex-col justify-center">
                        <div className="space-y-6 sm:space-y-8">
                            <div>
                                <h3 className="text-xl sm:text-2xl font-bold mb-3 sm:mb-4 text-foreground">{t('HOW_TO_CONTACT_US')}</h3>
                                <p className="text-sm sm:text-base text-muted-foreground leading-relaxed">
                                    {t('SUPPORT_MESSAGE')}
                                </p>
                            </div>

                            <div className="space-y-4 sm:space-y-6">
                                {/* <a
                                    href="tel:+41412446464"
                                    className="flex items-center min-h-[60px] sm:h-24 space-x-3 sm:space-x-4 p-3 sm:p-4 rounded-lg bg-white-25 border border-border/50 shadow-sm hover:bg-white-25 hover:border-border transition-all duration-200 cursor-pointer group"
                                >
                                    <div className="bg-accent p-2 sm:p-3 rounded-full group-hover:bg-accent/80 transition-colors flex-shrink-0">
                                        <Phone size={18} className="sm:w-5 sm:h-5 text-accent-foreground" />
                                    </div>
                                    <span className="text-base sm:text-lg text-foreground font-medium group-hover:text-primary transition-colors">+41 41 244 64 64</span>
                                </a> */}

                                <a
                                    href="mailto:contact@be-brave.ch"
                                    className="flex items-center min-h-[60px] sm:h-24 space-x-3 sm:space-x-4 p-3 sm:p-4 rounded-lg bg-white-25 border border-border/50 shadow-sm hover:bg-white-25 hover:border-border transition-all duration-200 cursor-pointer group"
                                >
                                    <div className="bg-accent p-2 sm:p-3 rounded-full group-hover:bg-accent/80 transition-colors flex-shrink-0">
                                        <Mail size={18} className="sm:w-5 sm:h-5 text-accent-foreground" />
                                    </div>
                                    <span className="text-base sm:text-lg text-foreground font-medium group-hover:text-primary transition-colors break-all">contact@be-brave.ch</span>
                                </a>

                                <div className="flex items-start space-x-3 sm:space-x-4 p-3 sm:p-4 rounded-lg bg-white-25 border border-border/50 shadow-sm">
                                    <div className="bg-accent p-2 sm:p-3 rounded-full mt-1 flex-shrink-0">
                                        <MapPin size={18} className="sm:w-5 sm:h-5 text-accent-foreground" />
                                    </div>
                                    <div>
                                        <p className="text-base sm:text-lg text-foreground font-medium">Blegi 3, 6343 Rotkreuz</p>
                                        <p className="text-base sm:text-lg text-foreground font-medium">{t('SWITZERLAND')}</p>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Right Side - Contact Form */}
                    <div className="p-4 sm:p-6 lg:p-8 lg:w-3/5 overflow-y-auto bg-background flex-1">
                        <div className="w-full space-y-4 sm:space-y-6">
                            {isSuccess ? (
                                /* Success Message */
                                <div className="text-center py-8 sm:py-12 px-4 sm:px-6">
                                    <div className="mb-4 sm:mb-6">
                                        <div className="mx-auto w-12 h-12 sm:w-16 sm:h-16 bg-green-100 dark:bg-green-900/20 rounded-full flex items-center justify-center mb-3 sm:mb-4">
                                            <CheckCircle className="w-6 h-6 sm:w-8 sm:h-8 text-green-600 dark:text-green-400" />
                                        </div>
                                        <h3 className="text-xl sm:text-2xl font-bold text-foreground mb-2">
                                            {t('MESSAGE_SENT_SUCCESSFULLY') || 'Message Sent Successfully!'}
                                        </h3>
                                        <p className="text-sm sm:text-base text-muted-foreground leading-relaxed max-w-md mx-auto">
                                            {t('CONTACT_SUCCESS_MESSAGE') || 'Thank you for reaching out! We\'ve received your message and our team will get back to you within 24 hours.'}
                                        </p>
                                    </div>

                                    <div className="space-y-3">
                                        <Button
                                            onClick={() => setIsSuccess(false)}
                                            variant="outline"
                                            className="w-full sm:w-auto"
                                        >
                                            <ArrowLeft className="w-4 h-4 mr-2" />
                                            {t('SEND_ANOTHER_MESSAGE') || 'Send Another Message'}
                                        </Button>
                                        <div className="text-center">
                                            <button
                                                onClick={onClose}
                                                className="text-sm text-muted-foreground hover:text-foreground transition-colors"
                                            >
                                                {t('CLOSE')}
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            ) : (
                                <>
                                    {/* Form Header */}
                                    <div className="text-center mb-6 sm:mb-8">
                                        <h4 className="text-lg sm:text-xl font-semibold text-foreground mb-2">{t('GET_IN_TOUCH')}</h4>
                                        <p className="text-sm sm:text-base text-muted-foreground">{t('FILL_OUT_FORM')}</p>
                                    </div>

                                    {/* Name and Company Row */}
                                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
                                        <div className="space-y-2">
                                            <label className="block text-sm font-semibold mb-2 text-foreground">
                                                {t('NAME')} *
                                            </label>
                                            <input
                                                required
                                                type="text"
                                                placeholder={t('ENTER_YOUR_NAME')}
                                                value={formData.name}
                                                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                                                className="w-full bg-background border border-input focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:border-ring rounded-lg px-4 py-3 text-sm text-foreground placeholder:text-muted-foreground shadow-sm transition-all duration-200 hover:border-ring/50"
                                            />
                                            {errors.name && <p className="text-destructive text-xs mt-1">{errors.name}</p>}
                                        </div>

                                        <div className="space-y-2">
                                            <label className="block text-sm font-semibold mb-2 text-foreground">
                                                {t('COMPANY')}{companyRequired && ' *'}
                                            </label>
                                            <input
                                                type="text"
                                                placeholder={t('ORGANIZATION_NAME_PLACEHOLDER')}
                                                value={formData.company}
                                                onChange={(e) => setFormData({ ...formData, company: e.target.value })}
                                                className="w-full bg-background border border-input focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:border-ring rounded-lg px-4 py-3 text-sm text-foreground placeholder:text-muted-foreground shadow-sm transition-all duration-200 hover:border-ring/50"
                                            />
                                            {errors.company && <p className="text-destructive text-xs mt-1">{errors.company}</p>}
                                        </div>
                                    </div>

                                    {/* Email and Phone Row */}
                                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
                                        <div className="space-y-2">
                                            <label className="block text-sm font-semibold mb-2 text-foreground">
                                                {t('EMAIL')} *
                                            </label>
                                            <input
                                                type="email"
                                                placeholder={t('ENTER_YOUR_EMAIL')}
                                                value={formData.email}
                                                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                                                className="w-full bg-background border border-input focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:border-ring rounded-lg px-4 py-3 text-sm text-foreground placeholder:text-muted-foreground shadow-sm transition-all duration-200 hover:border-ring/50"
                                            />
                                            {errors.email && <p className="text-destructive text-xs mt-1">{errors.email}</p>}
                                        </div>

                                        <div className="space-y-2">
                                            <label className="block text-sm font-semibold mb-4 text-foreground">
                                                {t('PHONE')}
                                            </label>
                                            <UIPhoneInput
                                                value={formData.phone as Value}
                                                onChange={(value: Value) =>
                                                    setFormData({ ...formData, phone: value as string })
                                                }
                                                placeholder={t('PHONE_NUMBER_PLACEHOLDER')}
                                                defaultCountry="CH"
                                                countryCallingCodeEditable={false}
                                                international
                                                className="w-full h-[46px] bg-background border border-input focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:border-ring rounded-lg px-4 py-3 text-sm text-foreground placeholder:text-muted-foreground shadow-sm transition-all duration-200 hover:border-ring/50"
                                            />
                                            {errors.phone && <p className="text-destructive text-xs mt-1">{errors.phone}</p>}
                                        </div>
                                    </div>

                                    {/* Message */}
                                    <div className="space-y-2">
                                        <label className="block text-sm font-semibold mb-2 text-foreground">
                                            {t('MESSAGE')} *
                                        </label>
                                        <textarea
                                            placeholder={t('MESSAGE_PLACEHOLDER')}
                                            value={formData.message}
                                            onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                                            rows={5}
                                            className="w-full bg-background border border-input focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:border-ring rounded-lg px-4 py-3 text-sm text-foreground placeholder:text-muted-foreground shadow-sm transition-all duration-200 hover:border-ring/50 resize-none"
                                        />
                                        {errors.message && <p className="text-destructive text-xs mt-1">{errors.message}</p>}
                                    </div>

                                    {/* Submit Button */}
                                    <div className="pt-0">
                                        <Button
                                            variant="default"
                                            onClick={handleSubmit}
                                            disabled={loading}
                                            className={`w-full h-11 bg-primary hover:bg-primary-600  font-semibold py-4 px-6 rounded-lg transition-all duration-200 flex items-center justify-center gap-2 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 ${loading ? "opacity-70 cursor-not-allowed" : ""
                                                }`}
                                        >
                                            {loading && <Loader2 size={16} className="animate-spin" />}
                                            {loading ? t('SENDING') : t('SEND')}
                                        </Button>
                                    </div>
                                </>
                            )}
                        </div>
                    </div>
                </div>
            </DialogContent>
        </Dialog>
    );
};

export default ContactModal;
